import { forwardRef } from 'react';
import { cx } from '@/lib/cx';
import { blockWheelSpin } from './numberGuard';

/**
 * An input that lives inside a table cell.
 *
 * Marks entry is the one screen in this app that is a spreadsheet rather than a
 * register: a teacher types forty numbers in a row and must never touch the
 * mouse. So the cell *is* the input — no drawer, no edit affordance to click —
 * and the keyboard does the moving.
 *
 * ── The navigation contract ──
 * Down / Enter → next row. Up → previous row. Both are what a typist reaches
 * for without thinking, and Enter is the one that matters: it is how a column
 * of numbers gets entered. Tab is left to the browser, which already moves to
 * the next focusable thing.
 *
 * The component reports the intent; the grid owns the refs and decides what
 * "next" means. That keeps this a leaf with no knowledge of its siblings.
 *
 * ── Two variants, one control ──
 * Homework evaluation grades on the same row it comments on, so a marks cell
 * and a remarks cell sit side by side and must be the same object: same height,
 * same ruling, same dirty tint, same keys moving down the column. `variant`
 * changes only what a cell holds — a figure, right-aligned and tabular, or a
 * line of prose, left-aligned and filling the column.
 *
 * Two behaviours follow the variant rather than being options, because getting
 * either backwards destroys work:
 *
 *  - **Select-on-focus is `number` only.** Arriving in a marks cell and typing
 *    should replace the mark, not append a digit to it. Doing the same to a
 *    remark would wipe a sentence the teacher wrote last week on the first
 *    keystroke after they tab into it.
 *  - **`type="text"`, not `type="number"`, for prose** — otherwise the browser
 *    silently discards anything non-numeric as it is typed.
 */

export interface GridInputProps {
  value: string;
  onChange: (value: string) => void;
  onNavigate?: (direction: 'up' | 'down') => void;
  /** `number` (default) for a figure; `text` for a line of prose. */
  variant?: 'number' | 'text';
  /** Draws the invalid rule. Pair with an explanation elsewhere in the row. */
  invalid?: boolean;
  /** Marks the cell as changed but not yet written. */
  dirty?: boolean;
  disabled?: boolean;
  ariaLabel: string;
  placeholder?: string;
  maxLength?: number;
}

export const GridInput = forwardRef<HTMLInputElement, GridInputProps>(function GridInput(
  { value, onChange, onNavigate, variant = 'number', invalid, dirty, disabled, ariaLabel, placeholder, maxLength },
  ref,
) {
  const numeric = variant === 'number';

  return (
    <input
      ref={ref}
      type={numeric ? 'number' : 'text'}
      inputMode={numeric ? 'decimal' : undefined}
      aria-label={ariaLabel}
      aria-invalid={invalid || undefined}
      disabled={disabled}
      value={value}
      placeholder={placeholder}
      maxLength={maxLength}
      onChange={(e) => onChange(e.target.value)}
      onKeyDown={(e) => {
        if (e.key === 'ArrowDown' || e.key === 'Enter') {
          e.preventDefault();
          onNavigate?.('down');
        } else if (e.key === 'ArrowUp') {
          e.preventDefault();
          onNavigate?.('up');
        }
      }}
      onFocus={numeric ? (e) => e.currentTarget.select() : undefined}
      /* The arrows are claimed above, but the wheel is not: the register
         scrolls, and a wheel over a focused cell would silently restate a mark
         the teacher had already entered. Numeric only — on the remarks cell
         there is nothing to spin, and blurring would cost the teacher their
         place mid-sentence. See numberGuard. */
      onWheel={numeric ? blockWheelSpin : undefined}
      className={cx(
        'h-7 rounded-sm border bg-surface px-1.5 text-sm text-ink',
        'transition-colors duration-100',
        'disabled:cursor-not-allowed disabled:bg-subtle disabled:text-faint',
        numeric
          ? // Spinners are useless for marks and steal a third of the field.
            'w-20 text-right tabular-nums [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none'
          : 'w-full text-left',
        invalid
          ? 'border-danger focus:border-danger'
          : dirty
            ? 'border-accent-line bg-accent-wash focus:border-accent'
            : 'border-line-strong hover:border-faint focus:border-accent',
      )}
    />
  );
});
